import {
  CollisionDetection,
  DragCancelEvent,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Coordinates } from '@dnd-kit/utilities';
import { useRef, useState } from 'react';
import { useMoveCatalogItem } from '../../../../../services/modules/catalogProducts/catalogProducts.mutations';
import { useCatalogView } from '../contexts/CatalogView.context';
import { KeyboardDropTarget } from '../utils/catalogDragAndDrop.types';
import {
  getIsCrossSectionDrop,
  getIsDrawableDrop,
  parseSectionDroppableId,
  resolveDropTarget,
  resolveFromSectionId,
  resolvePointerSide,
} from '../utils/catalogDragAndDrop.utils';
import { createCatalogKeyboardCoordinateGetter } from '../utils/catalogKeyboardCoordinates';
import { CatalogViewGroupBy, CatalogViewMode } from '../view/catalogView.types';
import { useCatalogEffectiveView } from './useCatalogEffectiveView';
import { useCatalogGroupParams } from './useCatalogGroupParams';
import { useCatalogListContextParams } from './useCatalogListContextParams';
import {
  CatalogActiveDragItem,
  CatalogDragItemData,
  CatalogDropIndicatorState,
  CatalogProductsDragAndDrop,
  UseCatalogProductsDragAndDropParams,
} from './useCatalogProductsDragAndDrop.types';
import { useCatalogSortParams } from './useCatalogSortParams';
import { useCatalogUrlFilters } from './useCatalogUrlFilters';

const MOUSE_ACTIVATION_DISTANCE_PX = 4;
const TOUCH_ACTIVATION_DELAY_MS = 200;
const TOUCH_ACTIVATION_TOLERANCE_PX = 5;

export const useCatalogProductsDragAndDrop = (
  params: UseCatalogProductsDragAndDropParams,
): CatalogProductsDragAndDrop => {
  const { catalogId, canPersist, isEnabled, rows, scrollParent } = params;

  const { filters, customerIds } = useCatalogUrlFilters();
  const contextParams = useCatalogListContextParams();
  const { gridDataInclude, effectiveMode } = useCatalogView();
  const { sortParams } = useCatalogSortParams({ catalogId, canPersist });
  const { groupParams } = useCatalogGroupParams({ catalogId, canPersist });
  const moveCatalogItem = useMoveCatalogItem();

  const { groupBy } = useCatalogEffectiveView({ catalogId, canPersist });
  const isManualGroupingCriterion = groupBy === CatalogViewGroupBy.MANUAL;

  const [activeItem, setActiveItem] = useState<CatalogActiveDragItem | null>(null);
  const [dropIndicator, setDropIndicator] = useState<CatalogDropIndicatorState | null>(null);

  const keyboardTargetRef = useRef<KeyboardDropTarget | null>(null);
  const pointerCoordinatesRef = useRef<Coordinates | null>(null);

  const trackPointerCollisionDetection: CollisionDetection = (args) => {
    pointerCoordinatesRef.current = args.pointerCoordinates;

    const sectionCollision = pointerWithin(args).find(
      (collision) => parseSectionDroppableId(String(collision.id)) !== null,
    );

    if (sectionCollision) {
      return [sectionCollision];
    }

    return closestCenter(args);
  };

  const keyboardCoordinateGetter = createCatalogKeyboardCoordinateGetter({
    getItemIds: () => rows.flatMap((row) => row.itemIds),
    getTarget: () => keyboardTargetRef.current,
    onTargetChange: (target) => {
      keyboardTargetRef.current = target;
    },
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: MOUSE_ACTIVATION_DISTANCE_PX },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: TOUCH_ACTIVATION_DELAY_MS,
        tolerance: TOUCH_ACTIVATION_TOLERANCE_PX,
      },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: keyboardCoordinateGetter }),
  );

  const isDraggingDisabled = !isEnabled || moveCatalogItem.isPending;
  const isListMode = effectiveMode === CatalogViewMode.LIST;

  const resetDragState = () => {
    keyboardTargetRef.current = null;
    pointerCoordinatesRef.current = null;
    setActiveItem(null);
    setDropIndicator(null);
  };

  const resolveOver = ({ over }: Pick<DragOverEvent, 'over'>): KeyboardDropTarget | null => {
    const keyboardTarget = keyboardTargetRef.current;

    if (keyboardTarget) {
      return keyboardTarget;
    }

    if (!over) {
      return null;
    }

    return {
      overId: String(over.id),
      side: resolvePointerSide({
        pointerCoordinates: pointerCoordinatesRef.current,
        overRect: over.rect,
        isListMode,
      }),
    };
  };

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as CatalogDragItemData | undefined;
    if (!data) {
      return;
    }

    const { productCard, listRow } = data;

    const activeId = String(event.active.id);

    setActiveItem({
      id: activeId,
      fromSectionId: resolveFromSectionId(rows, activeId),
      productCard,
      listRow: listRow && { leftBlock: listRow.leftBlock, rect: listRow.measureRect() },
    });
  };

  const updateDropIndicator = ({ active, over }: Pick<DragOverEvent, 'active' | 'over'>) => {
    const resolvedOver = resolveOver({ over });

    if (!resolvedOver || resolvedOver.overId === active.id) {
      setDropIndicator(null);
      return;
    }

    const activeId = String(active.id);
    const { overId, side } = resolvedOver;

    if (dropIndicator?.overId === overId && dropIndicator.side === side) {
      return;
    }

    const isDrawable = getIsDrawableDrop({
      activeId,
      isManualGroupingCriterion,
      overId,
      pointerSide: side,
      rows,
    });

    if (!isDrawable) {
      setDropIndicator(null);
      return;
    }

    setDropIndicator({ overId, side });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const resolvedOver = resolveOver({ over });
    const fromSectionId = activeItem?.fromSectionId;
    resetDragState();

    if (!resolvedOver) {
      return;
    }

    const activeId = String(active.id);
    const { overId, side: pointerSide } = resolvedOver;

    const target = resolveDropTarget({ activeId, overId, pointerSide, rows });
    if (!target) {
      return;
    }

    if (!fromSectionId) {
      return;
    }

    if (
      getIsCrossSectionDrop({
        activeId,
        isManualGroupingCriterion,
        overId,
        rows,
      })
    ) {
      return;
    }

    moveCatalogItem.mutate({
      catalogId,
      fromSectionId,
      sectionProductId: activeId,
      toSectionId: target.toSectionId,
      afterItemId: target.afterItemId,
      optimisticParams: {
        contextParams,
        sortParams,
        groupParams,
        filters,
        customerIds,
        gridDataInclude,
      },
    });
  };

  const handleDragCancel = (_event: DragCancelEvent) => {
    resetDragState();
  };

  const autoScroll = scrollParent
    ? { canScroll: (element: Element) => element === scrollParent }
    : true;

  return {
    isEnabled,
    isDraggingDisabled,
    activeItem,
    dropIndicator,
    dragAndDropContextProps: {
      sensors,
      collisionDetection: trackPointerCollisionDetection,
      autoScroll,
      onDragStart: handleDragStart,
      onDragMove: updateDropIndicator,
      onDragOver: updateDropIndicator,
      onDragEnd: handleDragEnd,
      onDragCancel: handleDragCancel,
    },
  };
};
